import _ from "lodash";
import moment from "moment";
import {
  getProductId,
  isMpfProduct,
  isProductAllowedForOrderType,
} from "./orderProductUtils";
import { getSelectedCustomValue } from "./commissionCustomConditionUtils";

export { isMpfProduct };

const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return !value.trim();
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const isPaymentRequired = (formValues) => !formValues?.forceUncertainPayment;

const isCreditCardPayBy = (payBy) => {
  if (!payBy) return false;
  if (Array.isArray(payBy)) return payBy.includes("Credit Card");
  return payBy === "Credit Card";
};

const requiredPayment =
  (label, t) =>
  (value, formValues) => {
    if (!isPaymentRequired(formValues)) return null;
    if (isEmpty(value)) return t(`${label} is required`);
    return null;
  };

const positivePayment =
  (label, t) =>
  (value, formValues) => {
    if (!isPaymentRequired(formValues)) return null;
    if (value === null || value === undefined || value === "") {
      return t(`${label} is required`);
    }
    if (Number(value) <= 0) return t(`${label} must be greater than 0`);
    return null;
  };

const paymentTermEitherOr =
  (t) =>
  (value, formValues) => {
    if (!isPaymentRequired(formValues)) return null;
    const payment = formValues?.payment || {};
    const hasTerm = !isEmpty(payment.paymentTerm);
    const hasUntilAge = !isEmpty(payment.paymentTermUntilAge);
    if (!hasTerm && !hasUntilAge) {
      return t("Payment Term (Yrs) or Payment Term (Until Age) is required");
    }
    if (hasTerm && Number(payment.paymentTerm) <= 0) {
      return t("Payment Term (Yrs) must be greater than 0");
    }
    return null;
  };

export const ORDER_CONFIRM_FIELD_LABELS = {
  type: "Order Type",
  client: "Client",
  product: "Product",
  mpfAccountType: "MPF Account Type",
  consultants: "Consultant",
  signedDate: "Sign Date",
  signedPlace: "Sign Place",
  signedBy: "Sign By",
  productVariations: "Product Variation",
  productCustomOptions: "Commission Custom Options",
  insured: "Insured",
  "payment.premium": "Premium",
  "payment.currency": "Currency",
  "payment.payMode": "Pay Mode",
  "payment.payStatus": "Pay Status",
  "payment.paymentTerm": "Payment Term (Yrs)",
  "payment.paymentTermUntilAge": "Payment Term (Until Age)",
  "payment.policyTerm": "Policy Term",
  "payment.levy": "Levy",
  "payment.payBy": "Initial Payment Methods",
  "payment.creditCardLast4": "Credit Card (Last 4 Digits)",
  "payment.initialPaymentCurrency": "Initial Payment Currency",
  "payment.initialPayment": "Initial Payment",
  "payment.renewalPayBy": "Renewal Payment Methods",
  "payment.sumInsured": "Sum Insured",
  "payment.payUpDate": "Paid Up Date",
  "payment.prePaymentCurrency": "Pre Payment Currency",
  "payment.prePayment": "Pre Payment",
  fnaSignDate: "FNA Sign Date",
  policyAckSignDate: "Policy Acknowledge Date",
  transferInEffectiveDate: "Transfer In Effective Date",
  lapsedSurrenderDate: "Lapsed/Surrender Date",
  transferOutDate: "Transfer Out Date",
  reinstateDate: "Reinstate Date",
  totalWithdrawalAmount: "Total Withdrawal Amount",
  remark: "Remarks",
  error: "Error",
  consultantLicenceError: "Consultant Licence",
};

export const buildOrderFormValidate = (t) => {
  const paymentRequired = requiredPayment;
  const paymentPositive = positivePayment;

  return {
    client: (value) => (!value ? t("Please select Client") : null),
    jointClient: (value, formValues) => {
      if (!value || !formValues?.client) return null;
      const clientId = formValues.client?._id ?? formValues.client;
      const jointClientId = value?._id ?? value;
      if (clientId && jointClientId && String(clientId) === String(jointClientId)) {
        return t("Joint client cannot be the same as primary client");
      }
      return null;
    },
    product: (value, formValues) => {
      if (!value) return t("Please select Product");
      const orderType = formValues.type ?? "NEW";
      const status = formValues.productSnapShot?.status;
      if (
        !isProductAllowedForOrderType(status, orderType, {
          existingOrder: Boolean(formValues._id),
          selectedProductId: getProductId(value),
          originalProductId: formValues.originalProductId,
        })
      ) {
        return t(
          "Selected product is not available for New Biz orders. Please choose an active product."
        );
      }
      return null;
    },
    mpfAccountType: (value, formValues) => {
      if (!isMpfProduct(formValues)) return null;
      if (isEmpty(value)) return t("MPF Account Type is required");
      return null;
    },
    consultants: (value) =>
      _.isEmpty(value) ? t("Please select Consultant") : null,
    signedDate: (value) => {
      if (!value) return t("Sign Date is required");
      if (moment(value).isAfter(moment())) {
        return t("Please make sure Signed Date is before today.");
      }
      return null;
    },
    signedPlace: (value) => (!value ? t("Sign Place is required") : null),
    signedBy: (value) => (!value ? t("Sign By is required") : null),
    productVariations: (value, formValues) => {
      const variations = formValues.productSnapShot?.variations;
      if (_.isEmpty(variations)) return null;

      const selected = value || [];
      for (const variation of variations) {
        const match = selected.find((item) => item.name === variation.name && item._id);
        if (!match) {
          return t(`Please select ${variation.name}`);
        }
      }
      return null;
    },
    productCustomOptions: (value, formValues) => {
      const requiredFields = formValues.commissionCustomFieldsMeta || [];
      if (!requiredFields.length) return null;

      const selected = value || [];
      for (const field of requiredFields) {
        if (!getSelectedCustomValue(selected, field.name)) {
          return t(`Please select ${field.name}`);
        }
      }
      return null;
    },
    "payment.premium": paymentPositive("Premium", t),
    "payment.currency": paymentRequired("Currency", t),
    "payment.payMode": paymentRequired("Pay Mode", t),
    "payment.paymentTerm": paymentTermEitherOr(t),
    "payment.paymentTermUntilAge": paymentTermEitherOr(t),
    "payment.levy": (value, formValues) => {
      if (!isPaymentRequired(formValues)) return null;
      if (value === null || value === undefined || value === "") {
        return t("Levy is required");
      }
      return null;
    },
    "payment.payBy": paymentRequired("Initial Payment Methods", t),
    "payment.creditCardLast4": (value, formValues) => {
      if (!isPaymentRequired(formValues)) return null;
      if (!isCreditCardPayBy(formValues?.payment?.payBy)) return null;
      if (isEmpty(value)) return null;
      if (!/^\d{4}$/.test(String(value).trim())) {
        return t("Credit Card (Last 4 Digits) must be exactly 4 digits");
      }
      return null;
    },
    "payment.initialPaymentCurrency": paymentRequired(
      "Initial Payment Currency",
      t
    ),
    consultantLicenceError: (value, formValues) => {
      if (!formValues?.checkConsultantLicenceOnConfirm) return null;
      if (formValues?.consultantLicenceChecking) {
        return t("Checking consultant licences...");
      }
      return value || null;
    },
    error: (value) => value || null,
  };
};
